import React, { createContext, useContext, useEffect, useState } from "react";
import { AuthEvent, authService } from "../services/authService";
import { AppRoute } from "../App";
import { useNavigate } from "react-router-dom";
import { usePrognosisRepository } from "../services/prognosisRepository";
import { TokenStorage } from "../utils/tokenStorage";
import { userInfoRepository } from "@/services/userInfoRepository";

interface AuthContextType {
  isAuthenticated: boolean|null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth должен использоваться внутри AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const navigate = useNavigate();
  const { clearAllPrognosis } = usePrognosisRepository();
  const { clearUserInfo } = userInfoRepository;
  const [isAuthenticated, setIsAuthenticated] = useState<boolean|null>(authService.isAuthenticated);
  const [refreshFailCount, setRefreshFailCount] = useState(0);
  
  // Инициализация состояния аутентификации при загрузке
  useEffect(() => {
    const initAuth = async () => {
      // Проверяем localStorage перед инициализацией
      if (TokenStorage.isTokenValid()) {
        // Токен еще действителен, устанавливаем состояние
        setIsAuthenticated(true);
        setRefreshFailCount(0);
      } else {
        await authService.initialize();
        setIsAuthenticated(authService.isAuthenticated);
      }
    };
    initAuth();
  }, []);

  useEffect(() => {
    const handleLogin = () => {
      setIsAuthenticated(true);
      setRefreshFailCount(0);
      navigate(AppRoute.Home);
    };
    const handleLogout = () => {
      setIsAuthenticated(false);
      setRefreshFailCount(0);
      navigate(AppRoute.Login);
      clearAllPrognosis();
      clearUserInfo();
    };
    const handleRegister = () => {
      setIsAuthenticated(true);
      setRefreshFailCount(0);
      navigate(AppRoute.Home);
    };
    const handleRefreshFailed = () => {
      const newFailCount = refreshFailCount + 1;
      setRefreshFailCount(newFailCount);
      
      // Разлогиниваем пользователя только после нескольких неудачных попыток
      if (newFailCount >= 3) {
        setIsAuthenticated(false);
        setRefreshFailCount(0);
        navigate(AppRoute.Login);
        clearAllPrognosis();
        clearUserInfo();
      } else {
        // При временных ошибках не разлогиниваем пользователя
        console.log(`Ошибка обновления токена (попытка ${newFailCount}/3), пользователь остается в системе`);
      }
    };

    const handleResetPassword = () => {
      navigate(AppRoute.Login);
    };

    const handleRefresh = () => {
      setIsAuthenticated(true);
      setRefreshFailCount(0); // Сбрасываем счетчик при успешном обновлении
    };

    authService.on(AuthEvent.SIGN_IN, handleLogin);
    authService.on(AuthEvent.SIGN_OUT, handleLogout);
    authService.on(AuthEvent.SIGN_UP, handleRegister);
    authService.on(AuthEvent.REFRESH, handleRefresh);
    authService.on(AuthEvent.RESET_PASSWORD, handleResetPassword);
    authService.on(AuthEvent.REFRESH_FAILED, handleRefreshFailed);

    return () => {
      authService.off(AuthEvent.SIGN_IN, handleLogin);
      authService.off(AuthEvent.SIGN_OUT, handleLogout);
      authService.off(AuthEvent.SIGN_UP, handleRegister);
      authService.off(AuthEvent.REFRESH, handleRefresh);
      authService.off(AuthEvent.RESET_PASSWORD, handleResetPassword);
      authService.off(AuthEvent.REFRESH_FAILED, handleRefreshFailed);
    };
  }, [navigate, clearAllPrognosis]);

  const value: AuthContextType = {
    isAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
